import { Camera } from "./render/camera";
import { ORE_HUES, oreLabel } from "./render/colour";
import { $, el } from "./ui/format";
import { hideLoading, setStage, showLoading } from "./ui/loading";

/*
 * The world atlas: map tiles (a zoom pyramid per layer, made by
 * scripts/geology_atlas.py) drawn under the shared camera. Only tiles in view
 * at the right zoom are fetched; while a sharper tile loads, its nearest
 * loaded ancestor is drawn scaled up. This is the shape of the live client's
 * ground: a persistent world is far too big to upload whole.
 */

interface Layer {
  key: string;
  title: string;
  cmap: string | null;
  low: string;
  high: string;
  ramp: string[];
  /** What the layer shows, in a sentence (optional). */
  note?: string;
}

interface Atlas {
  seed: number;
  /** Top-level size in pixels (one hex across per pixel). */
  width: number;
  height: number;
  tile: number;
  levels: number;
  /** The hex at top-level pixel (0, 0). */
  origin: [number, number];
  layers: Layer[];
}

/** Terrain legend, matching the atlas renderer's palette. */
const TERRAIN: [string, string][] = [
  ["Open", "#5c7046"],
  ["Forest", "#304e2e"],
  ["Hills", "#826e50"],
  ["Mountains", "#96918c"],
  ["Water", "#2c5480"],
  ["Ford", "#608caa"],
];

/** Resource hues, matching the atlas renderer (shared with the game's
 *  ore heatmap), named as goods. */
const ORES: [string, string][] = ORE_HUES.map(([o, c]) => [oreLabel(o), c]);

/** Rows of hexes are sqrt(3)/2 apart on screen. */
const ROW = 0.866;
/** Tiles kept in memory. */
const CACHE = 800;

class AtlasView {
  /** Where the camera was before Tab showed the whole world. */
  private before: [number, number, number] | null = null;
  private canvas = $("atlas-view") as HTMLCanvasElement;
  /** Shown (the atlas screen is up): drawing and keys only then. */
  active = true;
  /** Until the first view's tiles have all arrived, the loading screen. */
  private loading = true;
  private ctx = this.canvas.getContext("2d")!;
  private camera = new Camera();
  private dpr = 1;
  private layer: Layer;
  private tiles = new Map<string, HTMLImageElement>();
  private dirty = true;
  private pointer: { x: number; y: number } | null = null;
  private urlTimer = 0;

  constructor(
    private atlas: Atlas,
    private base: string,
  ) {
    const params = new URLSearchParams(location.search);
    this.layer = atlas.layers.find((l) => l.key === params.get("layer")) ?? atlas.layers[0];
    this.camera.minZoom = 0.01;
    this.camera.maxZoom = 64;
    this.resize();
    const [x, y, z] = ["x", "y", "z"].map((k) => Number(params.get(k)));
    if (params.has("z") && isFinite(x) && isFinite(y) && z > 0) this.camera.set(x, y, z * this.dpr);
    else this.fit(true);
    this.buildPanel();
    this.bindInput();
    window.addEventListener("resize", () => {
      this.resize();
      this.dirty = true;
    });
    let last = performance.now();
    const frame = (now: number) => {
      if (this.camera.update((now - last) / 1000)) this.dirty = true;
      last = now;
      if (this.dirty && this.active) {
        this.dirty = false;
        this.draw();
        this.saveUrl();
      }
      requestAnimationFrame(frame);
    };
    requestAnimationFrame(frame);
  }

  private resize(): void {
    this.dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.canvas.width = Math.round(this.canvas.clientWidth * this.dpr);
    this.canvas.height = Math.round(this.canvas.clientHeight * this.dpr);
    this.camera.resize(this.canvas.width, this.canvas.height);
  }

  private fit(immediate: boolean): void {
    this.camera.fit(0, 0, this.atlas.width, this.atlas.height, 0.03, immediate);
  }

  // ------------------------------------------------------------ tiles

  /** World pixels one tile covers at level `z`. */
  private span(z: number): number {
    return this.atlas.tile * 2 ** (this.atlas.levels - 1 - z);
  }

  private tile(z: number, x: number, y: number, request: boolean): HTMLImageElement | null {
    const key = `${this.layer.key}/${z}/${x}_${y}`;
    let img = this.tiles.get(key);
    if (img) {
      // Most recently used last.
      this.tiles.delete(key);
      this.tiles.set(key, img);
      return img.complete && img.naturalWidth > 0 ? img : null;
    }
    if (!request) return null;
    img = new Image();
    img.decoding = "async";
    img.onload = () => (this.dirty = true);
    img.src = `${this.base}/${key}.webp`;
    this.tiles.set(key, img);
    while (this.tiles.size > CACHE) {
      const oldest = this.tiles.keys().next().value as string;
      this.tiles.get(oldest)!.src = "";
      this.tiles.delete(oldest);
    }
    return null;
  }

  private draw(): void {
    const { ctx, camera, atlas } = this;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = "#07080a";
    ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    // The level whose pixels are at least screen-pixel sized.
    const top = atlas.levels - 1;
    const z = Math.max(0, Math.min(top, top + Math.ceil(Math.log2(camera.zoom * 0.9))));
    const s = this.span(z);
    const [x0, y0, x1, y1] = camera.bounds();
    const tx0 = Math.max(0, Math.floor(x0 / s));
    const ty0 = Math.max(0, Math.floor(y0 / s));
    const tx1 = Math.min(Math.ceil(atlas.width / s) - 1, Math.floor(x1 / s));
    const ty1 = Math.min(Math.ceil(atlas.height / s) - 1, Math.floor(y1 / s));
    // Hexes as crisp blocks once they're bigger than a few pixels.
    ctx.imageSmoothingEnabled = camera.zoom < 3;
    ctx.setTransform(camera.zoom, 0, 0, camera.zoom, camera.width / 2 - camera.x * camera.zoom, camera.height / 2 - camera.y * camera.zoom);
    // Zoomed-out tiles are padded past the world's edge: clip to it.
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, atlas.width, atlas.height);
    ctx.clip();
    for (let ty = ty0; ty <= ty1; ty++) {
      for (let tx = tx0; tx <= tx1; tx++) {
        this.drawTile(z, tx, ty);
      }
    }
    ctx.restore();
    if (this.loading) {
      let waiting = 0;
      for (let ty = ty0; ty <= ty1; ty++) for (let tx = tx0; tx <= tx1; tx++) if (!this.tile(z, tx, ty, false)) waiting++;
      const total = (ty1 - ty0 + 1) * (tx1 - tx0 + 1);
      setStage("Loading the map", 1 - waiting / Math.max(1, total));
      if (waiting === 0) {
        this.loading = false;
        hideLoading();
      }
    }
    // The world's edge.
    ctx.strokeStyle = "rgba(255,255,255,0.15)";
    ctx.lineWidth = 1 / camera.zoom;
    ctx.strokeRect(0, 0, atlas.width, atlas.height);
  }

  /** Draws tile (z, x, y), or the part of its nearest loaded ancestor. */
  private drawTile(z: number, x: number, y: number): void {
    const s = this.span(z);
    for (let k = 0; k <= z; k++) {
      const img = this.tile(z - k, x >> k, y >> k, k === 0);
      if (!img) continue;
      // World pixels per image pixel at the ancestor's level.
      const scale = this.span(z - k) / this.atlas.tile;
      const ox = (x * s - (x >> k) * this.span(z - k)) / scale;
      const oy = (y * s - (y >> k) * this.span(z - k)) / scale;
      const w = Math.min(s / scale, img.naturalWidth - ox);
      const h = Math.min(s / scale, img.naturalHeight - oy);
      if (w <= 0 || h <= 0) return;
      // Half a screen pixel of overlap hides seams between tiles.
      const bleed = 0.5 / this.camera.zoom;
      this.ctx.drawImage(img, ox, oy, w, h, x * s, y * s, w * scale + bleed, h * scale + bleed);
      return;
    }
  }

  // ------------------------------------------------------------ panel

  private buildPanel(): void {
    const { atlas } = this;
    const hexes = (atlas.width * Math.round(atlas.height / ROW)) / 1e6;
    $("atlas-subtitle").textContent =
      `Seed ${atlas.seed} · ${atlas.width.toLocaleString("en-US")} × ${Math.round(atlas.height / ROW).toLocaleString("en-US")} hexes (${Math.round(hexes)}M)`;
    const list = $("atlas-layers");
    list.replaceChildren(
      ...atlas.layers.map((l, i) => {
        const b = el("button", { role: "radio" }, ...(i < 9 ? [el("kbd", {}, String(i + 1))] : []), l.title) as HTMLButtonElement;
        b.addEventListener("click", () => this.setLayer(l));
        b.dataset.key = l.key;
        return b;
      }),
    );
    this.syncPanel();
    const panel = $("atlas-panel");
    $("atlas-sheet-btn").addEventListener("click", () => panel.classList.toggle("open"));
    $("atlas-panel-close").addEventListener("click", () => panel.classList.remove("open"));
  }

  private setLayer(l: Layer): void {
    this.layer = l;
    this.syncPanel();
    this.dirty = true;
  }

  private syncPanel(): void {
    for (const b of $("atlas-layers").children) {
      b.setAttribute("aria-checked", String((b as HTMLElement).dataset.key === this.layer.key));
    }
    const l = this.layer;
    let legend: HTMLElement;
    const swatches = (items: [string, string][]) =>
      el(
        "div",
        { class: "legend-row" },
        ...items.map(([name, c]) => el("span", { class: "legend-item" }, el("span", { class: "swatch", style: `background:${c}` }), name)),
      );
    if (l.key === "terrain") {
      legend = swatches(TERRAIN);
    } else if (l.key === "ores") {
      legend = swatches(ORES);
    } else if (l.ramp.length) {
      legend = el(
        "div",
        { class: "scale" },
        el("div", { class: "ramp", style: `background:linear-gradient(90deg,${l.ramp.join(",")})` }),
        el("div", { class: "ends" }, el("span", {}, l.low), el("span", {}, l.high)),
      );
    } else {
      legend = el("p", { class: "muted small" }, `${l.low} to ${l.high}: sea by depth, land by height.`);
    }
    const notes = l.note ? [el("p", { class: "muted small" }, l.note)] : [];
    $("atlas-legend").replaceChildren(legend, ...notes);
  }

  /** The hex under top-level pixel (x, y), as the atlas renderer maps them. */
  private hexAt(x: number, y: number): [number, number] {
    const r = Math.floor((y * 10000) / 8660);
    const q = Math.floor((2 * Math.floor(x) - r) / 2);
    return [this.atlas.origin[0] + q, this.atlas.origin[1] + r];
  }

  private readout(): void {
    if (!this.pointer) return;
    const [wx, wy] = this.camera.toWorld(this.pointer.x * this.dpr, this.pointer.y * this.dpr);
    const box = $("atlas-readout");
    if (wx < 0 || wy < 0 || wx >= this.atlas.width || wy >= this.atlas.height) {
      box.replaceChildren(el("p", { class: "muted" }, "Off the map."));
      return;
    }
    const [q, r] = this.hexAt(wx, wy);
    box.replaceChildren(
      el("p", {}, el("b", {}, `Hex ${q}, ${r}`)),
      el("p", { class: "muted small" }, `Zone ${q >> 8}, ${r >> 8} · ${this.layer.title}`),
    );
  }

  // ------------------------------------------------------------ input

  private bindInput(): void {
    const c = this.canvas;
    const pointers = new Map<number, { x: number; y: number }>();
    let pinch: { dist: number; mx: number; my: number } | null = null;
    const pinchState = () => {
      const [a, b] = [...pointers.values()];
      return { dist: Math.hypot(a.x - b.x, a.y - b.y), mx: (a.x + b.x) / 2, my: (a.y + b.y) / 2 };
    };
    c.addEventListener("pointerdown", (e) => {
      c.setPointerCapture(e.pointerId);
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      pinch = pointers.size === 2 ? pinchState() : null;
      c.classList.add("dragging");
    });
    c.addEventListener("pointermove", (e) => {
      this.pointer = { x: e.offsetX, y: e.offsetY };
      const p = pointers.get(e.pointerId);
      if (p) {
        if (pointers.size >= 2 && pinch) {
          pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
          const now = pinchState();
          this.camera.zoomNow(now.mx * this.dpr, now.my * this.dpr, now.dist / Math.max(1, pinch.dist));
          this.camera.panBy((now.mx - pinch.mx) * this.dpr, (now.my - pinch.my) * this.dpr);
          pinch = now;
        } else if (pointers.size === 1) {
          this.camera.panBy((e.clientX - p.x) * this.dpr, (e.clientY - p.y) * this.dpr);
          pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
        }
        this.dirty = true;
      }
      this.readout();
    });
    const up = (e: PointerEvent) => {
      pointers.delete(e.pointerId);
      if (pointers.size < 2) pinch = null;
      if (pointers.size === 0) c.classList.remove("dragging");
    };
    c.addEventListener("pointerup", up);
    c.addEventListener("pointercancel", up);
    c.addEventListener(
      "wheel",
      (e) => {
        e.preventDefault();
        const factor = Math.exp(-e.deltaY * (e.deltaMode === 1 ? 0.05 : 0.0015));
        this.camera.zoomAt(e.offsetX * this.dpr, e.offsetY * this.dpr, factor);
        this.dirty = true;
      },
      { passive: false },
    );
    c.addEventListener("dblclick", (e) => {
      this.camera.zoomAt(e.offsetX * this.dpr, e.offsetY * this.dpr, 3);
      this.dirty = true;
    });
    window.addEventListener("keydown", (e) => {
      if (!this.active || e.ctrlKey || e.metaKey || e.altKey) return;
      const k = e.key;
      if (k >= "1" && k <= "9" && Number(k) <= this.atlas.layers.length) this.setLayer(this.atlas.layers[Number(k) - 1]);
      else if (k === "Tab") {
        // The whole world, and again back (the same key as in battles).
        e.preventDefault();
        if (this.before) {
          this.camera.set(...this.before);
          this.before = null;
        } else {
          this.before = [this.camera.x, this.camera.y, this.camera.zoom];
          this.fit(false);
        }
      } else return;
      this.dirty = true;
    });
  }

  private saveUrl(): void {
    window.clearTimeout(this.urlTimer);
    this.urlTimer = window.setTimeout(() => {
      const url = new URL(location.href);
      url.searchParams.set("layer", this.layer.key);
      url.searchParams.set("x", this.camera.x.toFixed(0));
      url.searchParams.set("y", this.camera.y.toFixed(0));
      url.searchParams.set("z", (this.camera.zoom / this.dpr).toPrecision(3));
      if (this.active) history.replaceState(null, "", url);
    }, 300);
  }

  /** Back to the screen (the camera and loaded tiles kept). */
  resume(): void {
    this.active = true;
    this.resize();
    this.dirty = true;
  }
}

let view: AtlasView | null = null;

/** Opens the atlas screen (loading it the first time); `back` returns to
 *  where it was opened from (Esc or Menu). */
export async function openAtlas(back: () => void): Promise<void> {
  const screen = $("atlas-screen");
  screen.classList.remove("hidden");
  document.body.classList.add("in-atlas");
  const leave = () => {
    if (view) view.active = false;
    screen.classList.add("hidden");
    document.body.classList.remove("in-atlas");
    window.removeEventListener("keydown", esc, true);
    back();
  };
  const esc = (e: KeyboardEvent) => {
    if (e.key !== "Escape") return;
    e.preventDefault();
    e.stopImmediatePropagation();
    leave();
  };
  window.addEventListener("keydown", esc, true);
  $("atlas-menu").onclick = leave;
  if (view) {
    view.resume();
    return;
  }
  showLoading("World atlas", "Loading the atlas");
  try {
    const name = new URLSearchParams(location.search).get("atlas") || "world-2";
    if (!/^[\w-]+$/.test(name)) throw new Error(`Bad atlas name: ${name}`);
    const base = `atlas/${name}`;
    const res = await fetch(`${base}/atlas.json`, { cache: "no-cache" });
    if (!res.ok) throw new Error(`Couldn't load atlas "${name}": ${res.status}`);
    view = new AtlasView((await res.json()) as Atlas, base);
  } catch (e) {
    hideLoading();
    const box = $("atlas-error");
    box.textContent = e instanceof Error ? e.message : String(e);
    box.classList.remove("hidden");
  }
}
